import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChevronLeft, Download, Loader2 } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import createApiClient from '@/api/axiosInstance';

const ViewQuotationScreen = ({ setField, selectedQuoteId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const apiClient = createApiClient(navigate, dispatch);

  const [quotationData, setQuotationData] = useState();

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDecline, setIsLoadingDecline] = useState(false);

  useEffect(() => {
    const getQuoteData = async () => {
      try {
        const response = await apiClient.post(
          '/api/quotation/get/quote-number',
          JSON.stringify({
            quote_number: selectedQuoteId,
          }),
        );

        const responseData = response?.data;
        setQuotationData(responseData.data);
      } catch (error) {
        console.error('ERROR IN GETTING QUOTE DATA');
      }
    };

    getQuoteData();
  }, []);

  const DocumentCard = ({ fileName, fileSize, onDelete }) => {
    return (
      <div className="border border-gray-200 rounded-lg p-4 bg-white flex items-center space-x-3 max-w-xs">
        <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
          <div className="w-8 h-8 bg-gray-300 opacity-50"></div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 mb-1">{fileName}</p>
          <p className="text-xs text-gray-400">{fileSize}</p>
          <p className="text-xs text-darkerPurple hover:cursor-pointer">
            Download
          </p>
        </div>
        <button
          onClick={onDelete}
          className="text-gray-400 hover:text-red-500 p-1 flex-shrink-0"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            ></path>
          </svg>
        </button>
      </div>
    );
  };

  const handleActionButton = async (accept, decline) => {
    if (accept && !decline) {
      setIsLoading(true);
      try {
        const response = await apiClient.post(
          '/api/quotation/accept',
          JSON.stringify({
            quote_number: selectedQuoteId,
          }),
        );

        console.log('QUOTE ACCEPTED');
        setField('DASHBOARD');
      } catch (error) {
        console.error('ERROR IN ACCEPTING QUOTE');
      } finally {
        setIsLoading(false);
      }
    }

    if (decline && !accept) {
    }
  };

  return (
    <div className="">
      <div className="flex justify-between items-center">
        <div className="flex gap-4 items-center">
          <ChevronLeft
            className="h-5 w-5 hover:cursor-pointer"
            onClick={() => setField('DASHBOARD')}
          />
          <h1 className="text-lg font-medium">Quotation #{selectedQuoteId}</h1>
        </div>
        <div className="flex justify-end gap-4">
          <Button className="bg-white border border-darkerPurple rounded-full text-darkerPurple hover:bg-gray-50 transition-all ease-in duration-300">
            <Download className="h-6 w-6" />
          </Button>
          <Button
            variant="default"
            disabled={isLoading}
            className={`w-[130px] text-base text-white transition ease-in-out duration-300 ${
              isLoading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-darkPurple hover:bg-darkerPurple'
            } flex items-center justify-center`}
            onClick={() => {
              handleActionButton(true, false);
            }}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                Accepting...
              </>
            ) : (
              'Accept Quote'
            )}
          </Button>
          <Button
            variant="outline"
            disabled={isLoadingDecline}
            className={`w-[130px] text-base  transition ease-in-out duration-300 ${
              isLoadingDecline
                ? 'text-gray-400  border border-gray-400 cursor-not-allowed'
                : 'text-darkPurple border border-darkPurple hover:text-darkPurple hover:bg-purple-50'
            } flex items-center justify-center`}
            onClick={() => {
              handleActionButton(false, true);
            }}
          >
            {isLoadingDecline ? (
              <>
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                Declining...
              </>
            ) : (
              'Decline Quote'
            )}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-10 gap-y-4 mt-8">
        <div className="col-span-8 flex gap-4 items-center">
          <div className="text-sm text-gray-400">Manufacturer Name</div>
          <div className="text-md text-gray-600 font-semibold">
            {quotationData?.manufacturer_name}
          </div>
        </div>

        <div className="col-span-2 flex gap-4 items-center">
          <div className="text-sm text-gray-400">Quote Submit Date</div>
          <div className="text-md text-gray-600 font-semibold">
            {quotationData?.Quote_Submit_Name}
          </div>
        </div>

        <div className="col-span-8 flex gap-4 items-center">
          <div className="text-sm text-gray-400">Estimated Delivery Time</div>
          <div className="text-md text-gray-600 font-semibold">
            {quotationData?.Estimate_Delivery_Date}
          </div>
        </div>

        <div className="col-span-2 flex gap-4 items-center">
          <div className="text-sm text-gray-400">Quote Validity</div>
          <div className="text-md text-gray-600 font-semibold">
            {quotationData?.quote_validity}
          </div>
        </div>
      </div>

      <div className="mt-8 p-1 bg-gray-50 font-medium rounded-lg text-gray-500">
        Detailed Cost Breakdown
      </div>
      <div className="overflow-hidden bg-gray-50 rounded-lg mt-2">
        <div className="bg-veryVeryLightPurple grid grid-cols-customQ6 gap-4 py-2 text-sm text-center font-medium text-gray-700 mb-4">
          <div>Part Number</div>
          <div>Part Name</div>
          <div>Min Order Quantity (MOQ)</div>
          <div>Unit Price</div>
          <div>Total Price</div>
          <div>Lead Time (Days)</div>
        </div>
        <div className="grid grid-cols-customQ6 gap-4 py-4 text-center">
          <div>{quotationData?.part_number}</div>
          <div>{quotationData?.part_name}</div>
          <div>{quotationData?.min_order_quantity}</div>
          <div>${quotationData?.unit_price}</div>
          <div>${quotationData?.total_price}</div>
          <div>{quotationData?.lead_time_days}</div>
        </div>
      </div>
      <div className="mt-8 p-1 bg-gray-50 font-medium rounded-lg text-gray-500">
        Documents
      </div>
      <div className="mt-2 flex gap-6">
        <DocumentCard fileName="Document" fileSize="10 MB" />
        <DocumentCard fileName="Document" fileSize="10 MB" />
      </div>

      <div className="mt-8 p-1 bg-gray-50 font-medium rounded-lg text-gray-500">
        Coments
      </div>
      <div className="mt-2 flex justify-between ">
        <div>{quotationData?.manufacturer_name}</div>
        <Input
          className="w-3/4 h-24 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple mb-4"
          value={quotationData?.note_for_buyer}
        ></Input>
      </div>
    </div>
  );
};

export default ViewQuotationScreen;
